import {HttpParams} from '@angular/common/http';
import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit} from '@angular/core';
import {AbstractControl, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {NbWindowRef} from '@nebular/theme';
import {BaseComponent, Catalog, CatalogParentTree, CatalogWithAncestors, LazyCatalog, PageApiResponse} from 'compob2b-libs';
import {Observable, Subject} from 'rxjs';
import {catchError, debounceTime, distinctUntilChanged, filter, map, switchMap, take, takeUntil, tap} from 'rxjs/operators';
import {translations} from '../../../environments/translations.ru';
import {CatalogService} from '../../cabinet/pim/catalog/catalog.service';

@Component({
  templateUrl: './choose-catalog-modal.component.html',
  styleUrls: ['./choose-catalog-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ChooseCatalogModalComponent extends BaseComponent implements OnInit {
  @Input() catalogService: CatalogService;

  public onlyOneSelection = true;
  public chooseOnlyLastLevel = true;
  public catalogChosenEvent: EventEmitter<CatalogWithAncestors>;
  public removeCatalogEvent: EventEmitter<CatalogWithAncestors>;
  public chosenCatalogs: Catalog[] = [];
  public fullChosenCatalogs: CatalogWithAncestors[] = [];
  public chosenCatalog: CatalogWithAncestors = null;
  public excludeCatalogIds: number[] = [];

  public categories: CatalogWithAncestors[] = [];
  public loadingCategories = false;
  public catalogInput$ = new Subject<string>();

  public cascadeCategoryPath: LazyCatalog[] = [];

  public loadedElements = 0;
  public totalElements = 0;
  private pageSize = 30;
  private currentPage = 0;
  private numberOfItemsFromEndBeforeFetchingMore = 10;

  private searchTerm = '';

  form = new UntypedFormGroup({
    category: new UntypedFormControl(null, Validators.required)
  });

  categoriesByLevels: LazyCatalog[][] = [];

  constructor(
    protected readonly windowRef: NbWindowRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loading = true;
    this.catalogService.initCatalogTree(0)
      .pipe(take(1))
      .subscribe(rootCategories => {
        this.categoriesByLevels[0] = rootCategories;
        this.loading = false;
        this.cdr.detectChanges();

      });

    this.catalogInput$
      .pipe(
        tap(term => this.searchTerm = term),
        debounceTime(400),
        distinctUntilChanged(),
        filter(term => term !== null),
        tap(_ => this.clearPreviousSearch()),
        switchMap(term => this.searchCatalogsWithAncestors(term)),
        takeUntil(this.unsubscribe),
      )
      .subscribe(foundCategories => this.setFoundCategories(foundCategories));

    this.loadChosenCatalogs();
    this.loadChosenCatalog();
  }

  removeCatalog(catalog: CatalogWithAncestors): void {
    if (!catalog) {
      return;
    }

    const index = this.fullChosenCatalogs.findIndex(item => item.id === catalog.id);
    if (index !== -1) {
      this.fullChosenCatalogs.splice(index, 1);
    }
    this.cdr.detectChanges();

    if (this.removeCatalogEvent) {
      this.removeCatalogEvent.emit(catalog);
    }
  }

  compareCategories(a: CatalogWithAncestors, b: CatalogWithAncestors): boolean {
    return a.id === b.id;
  }

  changeSelectedCatalog(catalog: CatalogWithAncestors): void {
    if (!catalog) {
      return;
    }

    this.loadCatalogParentTree(catalog.id)
      .pipe(take(1))
      .subscribe(response => this.updateCatalogParentTree(response));
  }

  chooseCategory() {
    const catalog: CatalogWithAncestors = this.category.value;
    if (this.excludeCatalogIds.length > 0 && this.excludeCatalogIds.includes(catalog.id)) {
      this.warningToast(translations.catalogAlreadyChosen);
      return;
    }

    if (this.catalogChosenEvent) {
      this.catalogChosenEvent.emit(catalog);
    }
    this.clearSearch();

    if (this.onlyOneSelection) {
      this.windowRef.close();
    }

    const index = this.fullChosenCatalogs.findIndex(item => item.id === catalog.id);
    if (index === -1) {
      this.fullChosenCatalogs.push(catalog);
    }
    this.cdr.detectChanges();
  }

  getOptionLabel(catalog: CatalogWithAncestors): string {
    return catalog.ancestors.filter(item => item !== null).concat(catalog.header).join(' > ');
  }

  chooseCascadeCategory(category: LazyCatalog) {
    const activeCatalog: LazyCatalog = this.category.value;
    if (activeCatalog && activeCatalog.id === category.id) {
      return;
    }

    this.categoriesByLevels.splice(category.level - 1);

    this.cascadeCategoryPath.splice(category.level - 2);
    this.cascadeCategoryPath[category.level - 2] = category;

    const selectedCategory: CatalogWithAncestors = {
      id: category.id,
      header: category.header,
      lastLevel: category.lastLevel,
      ancestors: this.cascadeCategoryPath.map(item => item.header).slice(0, this.cascadeCategoryPath.length - 1)
    } as CatalogWithAncestors;
    this.category.patchValue(selectedCategory);

    if (category.lastLevel) {
      return;
    }

    this.catalogService.getLazyChildren(category.id).pipe(take(1)).subscribe(children => {
      this.categoriesByLevels[category.level - 1] = children;
      this.cdr.detectChanges();
    });
  }

  onScroll($event: { start: number; end: number }) {
    if (this.loadingCategories || this.loadedElements >= this.totalElements) {
      return;
    }

    if ($event.end + this.numberOfItemsFromEndBeforeFetchingMore >= this.loadedElements) {
      this.fetchMore();
    }
  }

  onScrollToEnd() {
    if (this.loadingCategories || this.loadedElements >= this.totalElements) {
      return;
    }

    this.fetchMore();
  }

  private fetchMore(): void {
    this.searchCatalogsWithAncestors(this.searchTerm, this.currentPage + 1)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(items => {
        this.categories = this.categories.concat(items);
        this.cdr.detectChanges();
      });
  }

  isActive(category: LazyCatalog): boolean {
    if (this.cascadeCategoryPath.length <= category.level - 2) {
      return false;
    }

    return this.cascadeCategoryPath[category.level - 2].id === category.id;
  }

  clearSearch(): void {
    this.category.patchValue(null);
    this.cascadeCategoryPath = [];
    this.categories = [];
    this.categoriesByLevels.splice(1);
    this.clearPreviousSearch();
  }

  isCategoryLevelAppropriate(): boolean {
    const catalog: LazyCatalog = this.category.value;
    return catalog && (this.chooseOnlyLastLevel && catalog.lastLevel || !this.chooseOnlyLastLevel);
  }

  private handleResponse(response: PageApiResponse<CatalogWithAncestors[]>) {
    this.totalElements = response.totalElements;
    this.currentPage = response.number >= response.totalPages ? response.totalPages - 1 : response.number;
    this.loadedElements = this.categories.length + response.numberOfElements;
  }

  private clearPreviousSearch(): void {
    this.categories = [];
    this.totalElements = 0;
    this.loadedElements = 0;
    this.currentPage = 0;
  }

  private setFoundCategories(categories: CatalogWithAncestors[]): void {
    this.categories = categories;
    this.cdr.detectChanges();
  }

  private loadCatalogParentTree(catalogID: number): Observable<CatalogParentTree> {
    const url = `${this.catalogService.apiUrl}/${catalogID}/full-parent-tree`;
    this.loadingCategories = true;
    return this.compoApiService.get<CatalogParentTree>(url)
      .pipe(
        map(response => response.data),
        tap(_ => this.loadingCategories = false),
        takeUntil(this.unsubscribe),
      );
  }

  private updateCatalogParentTree(tree: CatalogParentTree): void {
    this.categoriesByLevels.splice(1);
    for (const level of Object.keys(tree.catalogByLevels)) {
      this.categoriesByLevels[parseInt(level, 10) - 2] = tree.catalogByLevels[level];
    }

    this.cascadeCategoryPath = [];
    for (const levelCategories of this.categoriesByLevels) {
      const activeLevelCategory = levelCategories.find(elem => tree.activeCatalogIDs.includes(elem.id));
      if (activeLevelCategory) {
        this.cascadeCategoryPath.push(activeLevelCategory);
      }
    }

    this.cdr.detectChanges();
  }

  private searchCatalogsWithAncestors(keyword: string, page: number = 0): Observable<CatalogWithAncestors[]> {
    const url = `${this.catalogService.apiUrl}/search-with-ancestors/page/${page}/${this.pageSize}`;
    const params = new HttpParams().set('header', keyword);

    this.loadingCategories = true;
    return this.compoApiService.getWithParams<PageApiResponse<CatalogWithAncestors[]>>(url, params)
      .pipe(
        catchError(err => this.handleError(err)),
        tap(response => this.handleResponse(response.data)),
        map(response => response.data.content),
        tap(_ => this.loadingCategories = false),
      );
  }

  private getCatalogWithAncestorsForIDs(ids: number[]): Observable<CatalogWithAncestors[]> {
    const url = `${this.catalogService.apiUrl}/with-ancestors-for-ids`;
    const params = new HttpParams().set('ids', JSON.stringify(ids));

    return this.compoApiService.getWithParams<CatalogWithAncestors[]>(url, params)
      .pipe(
        map(response => response.data),
      );
  }

  private loadChosenCatalogs(): void {
    if (this.chosenCatalogs) {
      this.getCatalogWithAncestorsForIDs(this.chosenCatalogs.map(item => item.id))
        .pipe(
          takeUntil(this.unsubscribe),
        )
        .subscribe(catalogs => catalogs.forEach(catalog => {
          this.fullChosenCatalogs.push(catalog);
          this.cdr.detectChanges();
        }));
    }
  }

  private loadChosenCatalog(): void {
    if (this.chosenCatalog) {
      this.category.setValue(this.chosenCatalog);
      this.changeSelectedCatalog(this.chosenCatalog);
      this.cdr.detectChanges();
    }
  }

  get category(): AbstractControl {
    return this.form.get('category');
  }
}
